import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, ArrowRight, ArrowLeft, Eye, EyeOff, Users, Loader2, Mail } from 'lucide-react';
import PasswordStrengthChecklist from '../components/PasswordStrengthChecklist';
import { isPasswordStrong } from '../utils/passwordPolicy';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const WORK_TYPES = [
  'Bygg & Entreprenad',
  'Mark & Anläggning',
  'El & Tele',
  'VVS & Rör',
  'Måleri',
  'Plåt & Ventilation',
  'Rivning & Sanering',
  'Trädgård & Landskap',
  'Tak & Fasad',
  'Annan',
];

const TIERS = [
  {
    id: 'start', name: 'Start', basePrice: 1990, perEmployee: 199,
    desc: 'Grundpaketet för entreprenörer',
    features: ['Jobbkalkyl & material', 'Offert & PDF + e-post', 'Projekthantering', 'Kalender & påminnelser'],
  },
  {
    id: 'pro', name: 'Pro', basePrice: 2990, perEmployee: 149,
    desc: 'Mest populär — allt du behöver', highlighted: true,
    features: ['Allt i Start', 'Fakturering', 'Tidrapportering', 'Maskintimmar', 'ÄTA-hantering'],
  },
  {
    id: 'business', name: 'Business', basePrice: 4490, perEmployee: 99,
    desc: 'Full kontroll & support',
    features: ['Allt i Pro', 'Lönehantering & lönespec', 'Bokföring', 'Alla funktioner', 'Prioriterad support'],
  },
];

const CONTRACTS = [
  { months: '6', label: '6 mån', multiplier: 1.10, tag: '+10%' },
  { months: '12', label: '12 mån', multiplier: 1.0, tag: 'Standard' },
  { months: '24', label: '24 mån', multiplier: 0.85, tag: '-15%' },
];

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled');
  const [step, setStep] = useState(1);
  const [otpScreen, setOtpScreen] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationToken, setVerificationToken] = useState(null);
  const otpRefs = useRef([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    email: '', password: '', password_confirm: '', name: '',
    company_name: '', work_type: 'Bygg & Entreprenad',
  });

  const [plan, setPlan] = useState({
    tier: 'pro', contract: '12', employeeCount: 5,
  });

  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const currentTier = TIERS.find(t => t.id === plan.tier);
  const currentContract = CONTRACTS.find(c => c.months === plan.contract);
  const baseMonthly = Math.round(currentTier.basePrice * currentContract.multiplier);
  // 1 användare ingår i baspaketet. Endast extra användare prissätts.
  const extraEmployees = Math.max(0, plan.employeeCount - 1);
  const employeeCost = currentTier.perEmployee * extraEmployees;
  const totalMonthly = baseMonthly + employeeCost;

  // Resend cooldown countdown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const canGoNext = () => {
    if (step === 1) return form.name && form.email && form.password && form.password_confirm;
    if (step === 2) return form.company_name && form.work_type;
    return true;
  };

  const sendOtp = async (isResend = false) => {
    setOtpLoading(true);
    try {
      await axios.post(`${API_URL}/auth/register/send-otp`, {
        email: form.email,
        name: form.name,
      });
      setResendCooldown(30);
      if (isResend) {
        toast.success('Ny kod skickad till ' + form.email);
        setOtpDigits(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skicka koden');
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    const code = otpDigits.join('');
    if (code.length !== 6) {
      toast.error('Ange den 6-siffriga koden');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/register/verify-otp`, {
        email: form.email,
        code,
      });
      setVerificationToken(res.data.verification_token);
      toast.success('E-post verifierad!');
      setOtpScreen(false);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fel kod');
      setOtpDigits(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpChange = (idx, val) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...otpDigits];
    next[idx] = val;
    setOtpDigits(next);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKey = (idx, e) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setOtpDigits(next);
    const lastIdx = Math.min(pasted.length, 5);
    otpRefs.current[lastIdx]?.focus();
  };

  const nextStep = async () => {
    if (step === 1) {
      if (form.password !== form.password_confirm) {
        toast.error('Lösenorden matchar inte');
        return;
      }
      if (!isPasswordStrong(form.password)) {
        toast.error('Lösenordet uppfyller inte säkerhetskraven (12 tecken, versal, gemen, siffra, specialtecken)');
        return;
      }
      // If we already verified this email, skip OTP
      if (verificationToken) {
        setStep(2);
        return;
      }
      const ok = await sendOtp(false);
      if (ok) {
        setOtpScreen(true);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
      return;
    }
    setStep(s => Math.min(s + 1, 3));
  };

  const handleSubmit = async () => {
    if (!agreementAccepted) {
      toast.error('Du måste godkänna avtalet för att fortsätta');
      return;
    }
    setLoading(true);
    try {
      const planId = `${plan.tier}_${plan.contract}`;
      const response = await axios.post(`${API_URL}/auth/register`, {
        email: form.email,
        password: form.password,
        password_confirm: form.password_confirm,
        name: form.name,
        company_name: form.company_name,
        work_type: form.work_type,
        plan_id: planId,
        employee_count: plan.employeeCount,
        origin_url: window.location.origin,
        verification_token: verificationToken,
        agreement_accepted: true,
      });

      const { token, checkout_url } = response.data;

      // Token stored as fallback for Stripe redirect (cookie also set by backend)
      localStorage.setItem('token', token);

      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        toast.success('Konto skapat! Betalning kan slutföras senare.');
        window.location.href = '/dashboard';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registrering misslyckades');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left panel — visual */}
      <div className="hidden lg:block lg:w-[45%] relative bg-[#1A1A1A]">
        <img
          src="https://images.unsplash.com/photo-1761734979410-112c9bd55690?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsJTIwYXJjaGl0ZWN0dXJlJTIwY29uc3RydWN0aW9uJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzc0ODY4MDMwfDA&ixlib=rb-4.1.0&q=85"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
        <div className="absolute bottom-14 left-10 right-10">
          <img src="/operio-logo-2.png" alt="Operio" className="w-10 h-10 mb-4" />
          <h1 className="text-3xl font-bold text-white tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Allt du behöver för att driva ditt byggföretag
          </h1>
          <p className="text-white/60 mt-3 text-sm leading-relaxed max-w-md">
            Tidrapportering, material, fakturering och AI-stöd i en plattform. Starta med 14 dagars fri provperiod.
          </p>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col bg-[#FAFAFA]">
        {/* Progress bar */}
        <div className="px-8 pt-8">
          <div className="flex items-center gap-2 mb-1">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center gap-2 flex-1">
                <div className={`h-1.5 rounded-full flex-1 transition-all duration-300 ${s <= step ? 'bg-[#E8613A]' : 'bg-[#E5E0DA]'}`} />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-[11px] text-[#9B9490] font-medium uppercase tracking-wider mt-1">
            <span className={step >= 1 ? 'text-[#E8613A]' : ''}>Konto</span>
            <span className={step >= 2 ? 'text-[#E8613A]' : ''}>Företag</span>
            <span className={step >= 3 ? 'text-[#E8613A]' : ''}>Plan & Betalning</span>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-8 py-6">
          <div className="w-full max-w-md">
            {cancelled && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="cancelled-notice">
                Betalningen avbröts. Du kan slutföra registreringen nedan.
              </div>
            )}

            {/* OTP screen — shown after step 1, before step 2 */}
            {otpScreen && (
              <div className="space-y-5" data-testid="register-otp-screen">
                <div>
                  <div className="w-12 h-12 rounded-full bg-[#E8613A]/10 flex items-center justify-center mb-3">
                    <Mail size={20} className="text-[#E8613A]" />
                  </div>
                  <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Verifiera din e-post</h2>
                  <p className="text-sm text-[#6B6560] mt-2 leading-relaxed">
                    Vi har skickat en 6-siffrig kod till <strong className="text-[#1A1A1A]">{form.email}</strong>. Ange koden nedan för att fortsätta.
                  </p>
                </div>

                <div className="flex justify-between gap-2" onPaste={handleOtpPaste} data-testid="otp-input-group">
                  {otpDigits.map((d, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={d}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKey(i, e)}
                      data-testid={`otp-digit-${i}`}
                      className="w-12 h-14 text-center text-2xl font-bold bg-white border-2 border-[#E5E0DA] rounded-xl focus:outline-none focus:border-[#E8613A] focus:ring-2 focus:ring-[#E8613A]/10 transition-all"
                    />
                  ))}
                </div>

                <Button
                  onClick={verifyOtp}
                  disabled={otpLoading || otpDigits.join('').length !== 6}
                  data-testid="otp-verify-btn"
                  className="w-full h-11 bg-[#E8613A] hover:bg-[#D4532F] text-white font-medium"
                >
                  {otpLoading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Verifierar...</> : 'Verifiera kod'}
                </Button>

                <div className="flex items-center justify-between text-xs text-[#9B9490]">
                  <button
                    type="button"
                    onClick={() => {
                      setOtpScreen(false);
                      setOtpDigits(['', '', '', '', '', '']);
                    }}
                    className="hover:text-[#1A1A1A] flex items-center gap-1"
                    data-testid="otp-back-btn"
                  >
                    <ArrowLeft size={12} /> Ändra e-post
                  </button>
                  <button
                    type="button"
                    onClick={() => sendOtp(true)}
                    disabled={resendCooldown > 0 || otpLoading}
                    className="hover:text-[#1A1A1A] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="otp-resend-btn"
                  >
                    {resendCooldown > 0 ? `Skicka igen om ${resendCooldown}s` : 'Skicka koden igen'}
                  </button>
                </div>

                <p className="text-[11px] text-[#9B9490] text-center pt-2">
                  Hittar du inte mailet? Kolla skräpposten.
                </p>
              </div>
            )}

            {/* STEP 1 — Account */}
            {step === 1 && !otpScreen && (
              <div className="space-y-5" data-testid="register-step-1">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Skapa ditt konto</h2>
                  <p className="text-sm text-[#9B9490] mt-1">Steg 1 av 3 — Kontoinformation</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-medium uppercase tracking-wider text-[#9B9490]">Ditt namn</Label>
                    <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Johan Andersson" className="h-11 mt-1" data-testid="register-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium uppercase tracking-wider text-[#9B9490]">E-post</Label>
                    <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="din@epost.se" className="h-11 mt-1" data-testid="register-email-input" />
                  </div>
                  <div className="relative">
                    <Label className="text-xs font-medium uppercase tracking-wider text-[#9B9490]">Lösenord</Label>
                    <div className="relative mt-1">
                      <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} placeholder="Minst 12 tecken med versal, siffra, specialtecken" className="h-11 pr-10" data-testid="register-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9B9490] hover:text-[#1A1A1A]">
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {form.password && <PasswordStrengthChecklist password={form.password} />}
                  </div>
                  <div>
                    <Label className="text-xs font-medium uppercase tracking-wider text-[#9B9490]">Bekräfta lösenord</Label>
                    <Input type="password" value={form.password_confirm} onChange={e => set('password_confirm', e.target.value)} placeholder="Upprepa lösenord" className="h-11 mt-1" data-testid="register-password-confirm-input" />
                  </div>
                </div>
                <Button onClick={nextStep} disabled={!canGoNext() || otpLoading} data-testid="register-next-step-1"
                  className="w-full h-11 bg-[#E8613A] hover:bg-[#D4532F] text-white font-medium mt-2">
                  {otpLoading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Skickar kod...</> : (verificationToken ? <>Nästa <ArrowRight size={16} className="ml-2" /></> : <>Skicka verifieringskod <ArrowRight size={16} className="ml-2" /></>)}
                </Button>
              </div>
            )}

            {/* STEP 2 — Company */}
            {step === 2 && !otpScreen && (
              <div className="space-y-5" data-testid="register-step-2">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Ditt företag</h2>
                  <p className="text-sm text-[#9B9490] mt-1">Steg 2 av 3 — Företagsinformation</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-medium uppercase tracking-wider text-[#9B9490]">Företagsnamn</Label>
                    <Input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Ditt Företag AB" className="h-11 mt-1" data-testid="register-company-input" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium uppercase tracking-wider text-[#9B9490]">Typ av verksamhet</Label>
                    <select value={form.work_type} onChange={e => set('work_type', e.target.value)}
                      className="w-full h-11 mt-1 rounded-md border border-input bg-background px-3 text-sm" data-testid="register-worktype-select">
                      {WORK_TYPES.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-11">
                    <ArrowLeft size={16} className="mr-2" /> Tillbaka
                  </Button>
                  <Button onClick={nextStep} disabled={!canGoNext()} data-testid="register-next-step-2"
                    className="flex-1 h-11 bg-[#E8613A] hover:bg-[#D4532F] text-white font-medium">
                    Nästa <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 3 — Plan & Payment */}
            {step === 3 && (
              <div className="space-y-5" data-testid="register-step-3">
                <div>
                  <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Välj plan</h2>
                  <p className="text-sm text-[#9B9490] mt-1">Steg 3 av 3 — 14 dagars gratis provperiod</p>
                </div>

                {/* Contract length */}
                <div className="flex justify-center">
                  <div className="inline-flex bg-white border border-[#E5E0DA] rounded-lg p-1 gap-1">
                    {CONTRACTS.map(c => (
                      <button key={c.months} onClick={() => setPlan(p => ({ ...p, contract: c.months }))}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${plan.contract === c.months ? 'bg-[#1A1A1A] text-white' : 'text-[#9B9490] hover:text-[#1A1A1A]'}`}>
                        {c.label} <span className={`ml-1 ${c.multiplier < 1 ? 'text-green-400 font-bold' : c.multiplier > 1 ? 'text-amber-400' : ''}`}>{c.tag}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Plan cards */}
                <div className="grid grid-cols-3 gap-3">
                  {TIERS.map(tier => {
                    const adj = Math.round(tier.basePrice * currentContract.multiplier);
                    const sel = plan.tier === tier.id;
                    return (
                      <div key={tier.id} onClick={() => setPlan(p => ({ ...p, tier: tier.id }))}
                        data-testid={`register-tier-${tier.id}`}
                        className={`cursor-pointer rounded-xl p-4 transition-all border-2 ${
                          sel ? 'border-[#E8613A] bg-white ring-2 ring-[#E8613A]/10'
                            : 'border-[#E5E0DA] bg-white hover:border-[#C5BFB9]'
                        } ${tier.highlighted && !sel ? 'border-[#1A1A1A]' : ''}`}>
                        {tier.highlighted && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#E8613A]">Populär</span>
                        )}
                        <p className="text-sm font-bold mt-1">{tier.name}</p>
                        <p className="text-xl font-bold mt-1">{adj.toLocaleString('sv-SE')} <span className="text-xs font-normal text-[#9B9490]">kr/mån</span></p>
                        <p className="text-[10px] text-[#9B9490] mt-0.5">1 ingår · +{tier.perEmployee} kr/extra</p>
                        <ul className="mt-3 space-y-1">
                          {tier.features.slice(0, 3).map((f, i) => (
                            <li key={i} className="flex items-center gap-1.5 text-[11px] text-[#6B6560]">
                              <Check size={10} className="text-[#E8613A] flex-shrink-0" /> {f}
                            </li>
                          ))}
                          {tier.features.length > 3 && (
                            <li className="text-[10px] text-[#9B9490]">+{tier.features.length - 3} mer</li>
                          )}
                        </ul>
                      </div>
                    );
                  })}
                </div>

                {/* Employee count */}
                <div className="bg-white rounded-xl border border-[#E5E0DA] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={16} className="text-[#E8613A]" />
                    <span className="text-sm font-bold">Antal anställda</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <input type="range" min="1" max="100" value={plan.employeeCount}
                      onChange={e => setPlan(p => ({ ...p, employeeCount: parseInt(e.target.value) }))}
                      className="flex-1 accent-[#E8613A]" data-testid="register-employee-slider" />
                    <span className="text-lg font-bold min-w-[30px] text-center" data-testid="register-employee-count">{plan.employeeCount}</span>
                  </div>
                </div>

                {/* Summary + Submit */}
                <div className="bg-white rounded-xl border-2 border-[#E8613A]/20 p-4">
                  <p className="text-sm font-bold">{currentTier.name} — {plan.contract} mån</p>
                  <p className="text-xs text-[#9B9490] mt-1">
                    {baseMonthly.toLocaleString('sv-SE')} kr bas (1 ingår) {extraEmployees > 0 ? `+ ${extraEmployees} extra × ${currentTier.perEmployee} kr` : ''}
                  </p>
                  <p className="text-xl font-bold text-[#E8613A] mt-1" data-testid="register-total-price">
                    {totalMonthly.toLocaleString('sv-SE')} kr/mån <span className="text-xs font-normal text-[#9B9490]">exkl. moms</span>
                  </p>
                  <p className="text-[10px] text-[#9B9490] mt-1">14 dagars gratis provperiod</p>
                </div>

                {/* Agreement acceptance */}
                <div className="bg-white rounded-xl border border-[#E5E0DA] p-4">
                  <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="register-agreement-label">
                    <input
                      type="checkbox"
                      checked={agreementAccepted}
                      onChange={(e) => setAgreementAccepted(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-[#E8613A] cursor-pointer flex-shrink-0"
                      data-testid="register-agreement-checkbox"
                    />
                    <span className="text-xs text-[#1A1A1A] leading-relaxed">
                      Jag godkänner Operios{' '}
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                        className="text-[#E8613A] hover:underline font-medium"
                        data-testid="register-show-terms-btn"
                      >
                        avtalsvillkor
                      </button>
                      {' '}för <strong>{currentTier.name}</strong>-planen med <strong>{plan.contract} månaders</strong> bindningstid. Jag förstår att jag kan säga upp avtalet utan kostnad inom 14 dagar (under provperioden). Därefter debiteras kortet automatiskt månadsvis.
                    </span>
                  </label>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep(2)} className="h-11">
                    <ArrowLeft size={16} />
                  </Button>
                  <Button onClick={handleSubmit} disabled={loading || !agreementAccepted} data-testid="register-submit-btn"
                    className="flex-1 h-11 bg-[#E8613A] hover:bg-[#D4532F] text-white font-semibold disabled:opacity-50">
                    {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Skapar konto...</> : 'Godkänn & starta 14 dagars test'}
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-6 text-center">
              <p className="text-xs text-[#9B9490]">
                Har du redan konto?{' '}
                <Link to="/login" className="text-[#E8613A] hover:underline font-medium" data-testid="goto-login-link">Logga in</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Terms modal */}
      {showTerms && (
        <div
          className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center px-4 py-6 overflow-y-auto"
          onClick={() => setShowTerms(false)}
          data-testid="register-terms-modal"
        >
          <div
            className="max-w-xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#1A1A1A] text-white px-7 py-5 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Avtalsvillkor</h2>
              <button onClick={() => setShowTerms(false)} className="p-1 hover:bg-white/10 rounded" data-testid="register-close-terms-btn">
                <ArrowLeft size={16} className="rotate-45" />
              </button>
            </div>
            <div className="px-7 py-6 max-h-[60vh] overflow-y-auto space-y-3 text-xs text-[#6B6560] leading-relaxed">
              <p className="text-sm text-[#1A1A1A] font-medium">
                Plan: <strong>{currentTier.name}</strong> — <strong>{plan.contract} månaders</strong> bindningstid — <strong>{totalMonthly.toLocaleString('sv-SE')} kr/mån</strong> exkl. moms
              </p>
              <p>
                <strong className="text-[#1A1A1A]">1. Tjänsten.</strong> Operio AB ("Operio") tillhandahåller en SaaS-plattform för administration av entreprenadföretag. Kunden får icke-exklusiv åtkomst under prenumerationens löptid.
              </p>
              <p>
                <strong className="text-[#1A1A1A]">2. Provperiod & ångerrätt.</strong> De första 14 dagarna är en kostnadsfri provperiod. Kunden kan när som helst under provperioden säga upp avtalet utan kostnad via Inställningar → Prenumeration. Efter provperioden debiteras kortet automatiskt enligt vald plan.
              </p>
              <p>
                <strong className="text-[#1A1A1A]">3. Betalning.</strong> Prenumerationen faktureras månadsvis via Stripe från och med dag 15. Vid utebliven betalning pausas tjänsten tills kortuppgifterna uppdaterats.
              </p>
              <p>
                <strong className="text-[#1A1A1A]">4. Bindningstid.</strong> Bindningstiden är {plan.contract} månader från och med första debiterade månad. Uppsägning sker senast 30 dagar före bindningstidens utgång — annars förlängs avtalet automatiskt en period i taget.
              </p>
              <p>
                <strong className="text-[#1A1A1A]">5. Data & GDPR.</strong> Kundens data ägs av kunden. Operio behandlar personuppgifter enligt GDPR. Vid uppsägning kan all data exporteras under 30 dagar.
              </p>
              <p>
                <strong className="text-[#1A1A1A]">6. Ansvar.</strong> Operio strävar efter 99,5% upptid men ansvarar inte för indirekta skador. Maxansvar motsvarar 3 månaders avgift.
              </p>
              <p>
                <strong className="text-[#1A1A1A]">7. Tvist.</strong> Svensk lag gäller. Tvister avgörs i Skövde tingsrätt.
              </p>
            </div>
            <div className="px-7 py-4 border-t border-[#E5E0DA] bg-[#FAFAFA] flex items-center justify-end">
              <Button onClick={() => setShowTerms(false)} className="bg-[#1A1A1A] hover:bg-[#333] text-white h-10 px-5 rounded-full" data-testid="register-terms-close-bottom-btn">
                Stäng
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
